import {
  GA_CLIENT_ID_COOKIE,
  GA_DISABLE_PREFIX,
  GA_SESSION_COOKIE_PREFIX,
} from '@/constants/analytics';
import { isGtagAvailable } from '@/lib/analytics/gtag';

function gaDisableKey(measurementId: string): `${typeof GA_DISABLE_PREFIX}${string}` {
  return `${GA_DISABLE_PREFIX}${measurementId}`;
}

/** GA4 session cookie uses the measurement ID without the `G-` prefix. */
function gaSessionCookieName(measurementId: string): string {
  const idWithoutPrefix = measurementId.replace(/^G-/i, '');
  return `${GA_SESSION_COOKIE_PREFIX}${idWithoutPrefix}`;
}

function expireCookie(name: string): void {
  document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
}

/**
 * Removes first-party GA4 cookies catalogued for Cookie Settings.
 * Call when the user withdraws analytics consent.
 */
export function clearGoogleAnalyticsCookies(measurementId: string): void {
  if (typeof document === 'undefined') return;

  expireCookie(GA_CLIENT_ID_COOKIE);
  expireCookie(gaSessionCookieName(measurementId));
}

/**
 * Opt out of Google Analytics immediately (flag + Consent Mode update +
 * clear first-party GA cookies). Call when consent is withdrawn after gtag
 * may already be loaded.
 */
export function disableGoogleAnalytics(measurementId: string): void {
  if (typeof window === 'undefined') return;

  window[gaDisableKey(measurementId)] = true;

  if (isGtagAvailable()) {
    window.gtag!('consent', 'update', { analytics_storage: 'denied' });
  }

  clearGoogleAnalyticsCookies(measurementId);
}

/**
 * Clear the opt-out flag, grant Consent Mode analytics storage, and re-config
 * when gtag is already present (remount alone may not re-run next/script).
 */
export function enableGoogleAnalytics(measurementId: string): void {
  if (typeof window === 'undefined') return;

  window[gaDisableKey(measurementId)] = false;

  if (isGtagAvailable()) {
    window.gtag!('consent', 'update', { analytics_storage: 'granted' });
    window.gtag!('config', measurementId);
  }
}
